import React, { useState, useContext } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useUser } from '../contexts/UserContext'
import { useFavorites } from '../contexts/FavoritesContext'
import { SidebarContext } from '../App'


import { 
  MessageCircle, 
  User, 
  LogOut, 
  Zap, 
  Menu,
  X,
  Heart,
  Palette,
  Search,
  Layers,
  Wand2,
  Settings
} from 'lucide-react'
import BrandLogo from './BrandLogo'
import { showsUpgradeOffer } from '../data/plansConfig'
import { isAdminUser } from '../utils/userProfileFields'

const Header = ({ onChatToggle, onAuthClick, onPremiumClick, onFavoritesClick }) => {
  const location = useLocation()
  const isDarkApp = !['/', '/privacy', '/terms'].includes(location.pathname)
  const { user, isPremium, logout, upgradeToPremium } = useUser()
  const { favorites } = useFavorites()
  const { isMobileMenuOpen, setIsMobileMenuOpen } = useContext(SidebarContext)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)

  const handleLogout = () => {
    logout()
    setIsUserMenuOpen(false)
  }

  const handleUpgradeClick = () => {
    if (!user) {
      onAuthClick('signup')
      return
    }
    onPremiumClick()
  }

  return (
    <header className={`sticky top-0 z-40 border-b ${
      isDarkApp
        ? 'bg-ink-soft/95 border-ivory/10 backdrop-blur-md'
        : 'glass-effect border-white/20'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center group" aria-label="Style AI home">
              <BrandLogo size="header" className="opacity-95 group-hover:opacity-100 transition-opacity" />
            </Link>
          </div>

          {/* Desktop Navigation - Removed duplicate links since they're in sidebar */}

          {/* Right side */}
          <div className="flex items-center space-x-4">
            {/* Favorites Button */}
            <button
              onClick={onFavoritesClick}
              className={`relative p-3 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md ${
                isDarkApp
                  ? 'text-ivory-muted hover:text-gold hover:bg-ivory/10'
                  : 'text-gray-600 hover:text-gold-light hover:bg-white/50'
              }`}
              title="Favorites"
            >
              <Heart className="w-5 h-5" />
              {favorites.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-gold text-ink text-xs font-semibold rounded-full w-5 h-5 flex items-center justify-center">
                  {favorites.length > 99 ? '99+' : favorites.length}
                </span>
              )}
            </button>

            {/* Chat Button */}
            <button
              onClick={onChatToggle}
              className={`p-3 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md ${
                isDarkApp
                  ? 'text-ivory-muted hover:text-gold hover:bg-ivory/10'
                  : 'text-gray-600 hover:text-gold-light hover:bg-white/50'
              }`}
              title="Open AI Assistant"
            >
              <MessageCircle className="w-5 h-5" />
            </button>

            {/* Pro badge */}
            {isPremium && (
              <div className="premium-badge flex items-center space-x-1">
                <span>{isAdminUser(user) ? 'Admin' : user?.isTester === true ? 'Tester' : 'Pro'}</span>
              </div>
            )}
            
            {/* Auth Status Indicator (dev only) - hidden on mobile */}
            {process.env.NODE_ENV === 'development' && (
              <div className="hidden md:block text-xs text-gray-500">
                {user ? `Signed in as ${user.email}` : 'Not signed in'}
              </div>
            )}

            {/* User Menu */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-3 p-3 text-gray-700 hover:text-gold-light hover:bg-white/50 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <div className="w-8 h-8 rounded-full border border-gold/40 bg-gold/10 flex items-center justify-center">
                    <User className="w-4 h-4 text-gold" />
                  </div>
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-3 w-56 glass-effect rounded-2xl shadow-2xl py-2 z-50 border border-white/20">
                    <div className="px-4 py-3 text-sm text-gray-700 border-b border-white/20">
                      <div className="font-medium">{user.email}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {isAdminUser(user) ? 'Style AI Admin' : user.isTester === true ? 'QA Test Account' : 'Style AI User'}
                      </div>
                    </div>
                    {showsUpgradeOffer(user, isPremium) && (
                      <button
                        onClick={handleUpgradeClick}
                        className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-white/30 flex items-center space-x-3 transition-all duration-200"
                      >
                        <Zap className="w-4 h-4 text-gold" />
                        <span>Upgrade to Pro</span>
                      </button>
                    )}
                    <Link
                      to="/settings"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-white/30 flex items-center space-x-3 transition-all duration-200"
                    >
                      <Settings className="w-4 h-4 text-gray-500" />
                      <span>Settings</span>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-white/30 flex items-center space-x-3 transition-all duration-200"
                    >
                      <LogOut className="w-4 h-4 text-gray-500" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-2 sm:space-x-3">
                <button 
                  onClick={() => onAuthClick('signin')}
                  className="button-secondary text-xs sm:text-sm px-3 sm:px-6 py-2 sm:py-3"
                >
                  Sign In
                </button>
                <button 
                  onClick={() => onAuthClick('signup')}
                  className="button-primary text-xs sm:text-sm px-3 sm:px-6 py-2 sm:py-3"
                >
                  Sign Up
                </button>
              </div>
            )}

            {/* Mobile menu button - opens sidebar */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-gray-600 hover:text-gold-light hover:bg-white/50 rounded-xl transition-all duration-200"
              title="Menu"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

    </header>
  )
}

export default Header 